import { useEffect, useMemo, useState } from "react";
import { Calendar as CalIcon, Plus, X, Loader2, ChevronLeft, ChevronRight, Clock, Trash2, Phone, ExternalLink, Video, User } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

function startOfMonth(d: Date) { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function addMonths(d: Date, n: number) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }
function sameDay(a: Date, b: Date) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

export default function BankerCalendar() {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [events, setEvents] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    setLoading(true);
    const monthStart = startOfMonth(cursor);
    const monthEnd = new Date(monthStart); monthEnd.setMonth(monthEnd.getMonth() + 1); monthEnd.setDate(0); monthEnd.setHours(23, 59, 59, 999);
    try {
      const [r, rc] = await Promise.all([
        fetch(`${BASE}api/banker/me/calendar?from=${encodeURIComponent(monthStart.toISOString())}&to=${encodeURIComponent(monthEnd.toISOString())}`, { headers: { "x-banker-token": token } }),
        companies.length === 0
          ? fetch(`${BASE}api/banker/companies`, { headers: { "x-banker-token": token } })
          : Promise.resolve(null),
      ]);
      if (r.ok) setEvents(await r.json());
      if (rc && rc.ok) setCompanies((await rc.json()).filter((c: any) => c.covered));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor]);

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const startWeekday = first.getDay(); // 0 = Sunday
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const e of events) {
      const d = new Date(e.startsAt);
      const key = d.toDateString();
      const arr = m.get(key) || [];
      arr.push(e);
      m.set(key, arr);
    }
    return m;
  }, [events]);

  const dayEvents = eventsByDay.get(selectedDay.toDateString()) || [];

  const remove = async (id: number | string) => {
    // Virtual scheduled-call rows ("sc-123") are owned by the call lifecycle,
    // not the manual calendar — they can only be cancelled via the call
    // cancel flow on the task detail page.
    if (typeof id === "string" && id.startsWith("sc-")) return;
    const token = localStorage.getItem("banker_token") || "";
    await fetch(`${BASE}api/banker/me/calendar/${id}`, { method: "DELETE", headers: { "x-banker-token": token } });
    await load();
  };

  const googleCalendarUrl = (e: any): string | null => {
    if (!e.googleEventId) return null;
    // Deep-link to the event in Google Calendar. eid is base64url(eventId + " " + calendarId).
    try {
      const calId = e.googleCalendarId || "";
      const raw = `${e.googleEventId} ${calId}`.trim();
      const eid = btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      return `https://calendar.google.com/calendar/event?eid=${eid}`;
    } catch { return null; }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2"><CalIcon className="w-7 h-7 text-[#FF5C00]" /> Calendar</h1>
          <p className="text-white/50 text-sm mt-1">Founder calls, internal reviews, follow-up reminders. Tied to the portcos you cover.</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" /> New event
        </button>
      </div>

      <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-4 md:p-5">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setCursor(addMonths(cursor, -1))} className="p-1.5 hover:bg-white/[0.06] rounded text-white/70"><ChevronLeft className="w-4 h-4" /></button>
          <h2 className="text-base font-semibold">{cursor.toLocaleString("en-US", { month: "long", year: "numeric" })}</h2>
          <button onClick={() => setCursor(addMonths(cursor, 1))} className="p-1.5 hover:bg-white/[0.06] rounded text-white/70"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-[10px] uppercase text-white/40 font-mono mb-1">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="text-center py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {grid.map((d, i) => {
            if (!d) return <div key={i} className="h-20 rounded bg-white/[0.02]" />;
            const evs = eventsByDay.get(d.toDateString()) || [];
            const isSel = sameDay(d, selectedDay);
            const isToday = sameDay(d, new Date());
            return (
              <button key={i} onClick={() => setSelectedDay(d)}
                className={`h-20 rounded p-1.5 text-left transition-colors border ${isSel ? "bg-[#FF5C00]/15 border-[#FF5C00]/40" : isToday ? "bg-white/[0.06] border-white/15" : "bg-white/[0.02] border-transparent hover:border-white/10"}`}>
                <div className={`text-xs font-mono ${isToday ? "text-[#FF5C00] font-bold" : "text-white/70"}`}>{d.getDate()}</div>
                {evs.slice(0, 2).map((e: any) => {
                  const isCall = e.kind === "call";
                  const isTentative = e.tentative;
                  return (
                    <div key={e.id} className={`text-[9px] truncate rounded px-1 mt-0.5 flex items-center gap-1 ${
                      isCall
                        ? isTentative
                          ? "text-blue-200/70 bg-blue-500/10 border border-dashed border-blue-400/30"
                          : "text-blue-200 bg-blue-500/15"
                        : "text-white/70 bg-[#FF5C00]/10"
                    }`}>
                      {isCall && <Phone className="w-2 h-2 flex-shrink-0" />}
                      <span className="truncate">{e.title}</span>
                    </div>
                  );
                })}
                {evs.length > 2 && <div className="text-[9px] text-white/40">+{evs.length - 2}</div>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-5">
        <h3 className="font-semibold text-white mb-3">{selectedDay.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</h3>
        {loading ? <p className="text-white/40 text-sm">Loading...</p>
          : dayEvents.length === 0 ? <p className="text-white/40 text-sm">No events scheduled.</p>
          : <div className="space-y-2">
            {dayEvents.map(e => {
              const isCall = e.kind === "call";
              const isTentative = e.tentative;
              const gcalUrl = isCall ? googleCalendarUrl(e) : null;
              return (
                <div key={e.id} className={`border rounded-lg p-3 flex items-start gap-3 ${
                  isCall
                    ? isTentative
                      ? "bg-blue-500/[0.04] border-blue-500/20 border-dashed"
                      : "bg-blue-500/[0.06] border-blue-500/25"
                    : "bg-white/[0.04] border-white/[0.08]"
                }`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${
                    isCall ? "bg-blue-500/15 border-blue-500/30" : "bg-[#FF5C00]/15 border-[#FF5C00]/30"
                  }`}>
                    {isCall ? <Phone className={`w-4 h-4 ${isTentative ? "text-blue-300/70" : "text-blue-300"}`} /> : <Clock className="w-4 h-4 text-[#FF5C00]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white text-sm font-medium">{e.title}</p>
                      {isTentative && (
                        <span className="text-[9px] uppercase tracking-wider text-blue-300/80 bg-blue-500/10 border border-blue-400/30 rounded px-1.5 py-0.5">
                          tentative
                        </span>
                      )}
                    </div>
                    <p className="text-white/50 text-xs mt-0.5">
                      {new Date(e.startsAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                      {e.endsAt && ` – ${new Date(e.endsAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
                      {e.company && ` · ${e.company.name}`}
                      {!isCall && ` · ${e.kind}`}
                      {isCall && e.investor?.email && ` · ${e.investor.email}`}
                    </p>
                    {e.notes && <p className="text-white/65 text-xs mt-1.5 leading-relaxed whitespace-pre-wrap">{e.notes}</p>}
                    {isCall && (
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        {e.meetUrl && (
                          <a href={e.meetUrl} target="_blank" rel="noopener noreferrer"
                            className="text-blue-300 hover:text-blue-200 text-xs font-medium flex items-center gap-1.5">
                            <Video className="w-3 h-3" /> Join call
                          </a>
                        )}
                        {gcalUrl && (
                          <a href={gcalUrl} target="_blank" rel="noopener noreferrer"
                            className="text-white/50 hover:text-white text-xs flex items-center gap-1.5">
                            <ExternalLink className="w-3 h-3" /> Open in Google Calendar
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  {e.source !== "google" && (
                    <button onClick={() => remove(e.id)} className="text-white/40 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              );
            })}
          </div>}
      </div>

      {createOpen && <CreateEventModal day={selectedDay} companies={companies} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
    </div>
  );
}

function CreateEventModal({ day, companies, onClose, onCreated }: { day: Date; companies: any[]; onClose: () => void; onCreated: () => void }) {
  const ymd = day.toISOString().slice(0, 10);
  // mode: "block" = generic free/busy, "call" = real call w/ investor (mints Meet)
  const [mode, setMode] = useState<"block" | "call">("block");
  const [form, setForm] = useState({
    title: "", notes: "", kind: "meeting",
    companyId: "" as any,
    investorId: "" as any,
    date: ymd, time: "10:00", duration: 30,
    reminderMinutes: 15,
    autoCreateMeet: true,
    meetingLink: "",
  });
  const [investors, setInvestors] = useState<any[]>([]);
  const [investorsLoading, setInvestorsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Lazy-fetch the banker's covered investors the first time they switch
  // into "Call" mode — keeps the modal open snappy for the common
  // free/busy block case.
  useEffect(() => {
    if (mode !== "call" || investors.length > 0 || investorsLoading) return;
    const token = localStorage.getItem("banker_token") || "";
    setInvestorsLoading(true);
    fetch(`${BASE}api/banker/investors`, { headers: { "x-banker-token": token } })
      .then(r => r.ok ? r.json() : [])
      .then((rows) => setInvestors(Array.isArray(rows) ? rows : []))
      .catch(() => {})
      .finally(() => setInvestorsLoading(false));
  }, [mode, investors.length, investorsLoading]);

  const save = async () => {
    if (!form.title || !form.date || !form.time) { setErr("Title, date and time are required"); return; }
    if (mode === "call" && !form.investorId) { setErr("Pick the investor for this call"); return; }
    if (mode === "call" && !form.autoCreateMeet && !form.meetingLink.trim()) {
      setErr("Paste a meeting link or turn auto-Meet back on"); return;
    }
    setSaving(true); setErr(null);
    const token = localStorage.getItem("banker_token") || "";
    const startsAt = new Date(`${form.date}T${form.time}`);
    try {
      let r: Response;
      if (mode === "call") {
        r = await fetch(`${BASE}api/banker/me/calendar/schedule-call`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-banker-token": token },
          body: JSON.stringify({
            title: form.title,
            scheduledFor: startsAt.toISOString(),
            durationMin: form.duration,
            investorId: Number(form.investorId),
            companyId: form.companyId ? Number(form.companyId) : null,
            agenda: form.notes || null,
            autoCreateMeet: form.autoCreateMeet,
            meetingLink: form.autoCreateMeet ? null : form.meetingLink,
          }),
        });
      } else {
        const endsAt = new Date(startsAt.getTime() + form.duration * 60000);
        r = await fetch(`${BASE}api/banker/me/calendar`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-banker-token": token },
          body: JSON.stringify({
            title: form.title, notes: form.notes || null, kind: form.kind,
            companyId: form.companyId ? Number(form.companyId) : null,
            startsAt: startsAt.toISOString(),
            endsAt: endsAt.toISOString(),
            reminderMinutes: form.reminderMinutes || null,
          }),
        });
      }
      if (r.ok) onCreated();
      else { const e = await r.json().catch(() => ({})); setErr(e.error || "Failed"); }
    } catch { setErr("Network error"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="force-dark-surface bg-[#111] border border-white/[0.1] rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><CalIcon className="w-5 h-5 text-[#FF5C00]" /> New event</h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Mode toggle: block-on-calendar vs schedule-investor-call */}
        <div className="grid grid-cols-2 gap-2 mb-4 p-1 bg-white/[0.03] border border-white/10 rounded-lg">
          <button
            type="button"
            onClick={() => setMode("block")}
            className={`px-3 py-2 rounded text-xs font-medium flex items-center justify-center gap-2 transition-colors ${mode === "block" ? "bg-[#FF5C00] text-white" : "text-white/60 hover:text-white"}`}
          >
            <Clock className="w-3.5 h-3.5" /> Block on calendar
          </button>
          <button
            type="button"
            onClick={() => setMode("call")}
            className={`px-3 py-2 rounded text-xs font-medium flex items-center justify-center gap-2 transition-colors ${mode === "call" ? "bg-blue-500 text-white" : "text-white/60 hover:text-white"}`}
          >
            <Phone className="w-3.5 h-3.5" /> Call with investor
          </button>
        </div>
        <p className="text-[11px] text-white/40 mb-3 -mt-1">
          {mode === "block"
            ? "Generic free/busy entry on your calendar — nobody else gets notified."
            : "Creates an advisor task + sends a Google Calendar invite (with Meet link) to the investor."}
        </p>

        {err && <div className="bg-red-500/10 border border-red-500/20 rounded p-2 text-red-400 text-xs mb-3">{err}</div>}
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-white/60 text-xs">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={mode === "call" ? "Intro call — portfolio fit" : "Founder call — Q3 review"}
              className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white" />
          </div>

          {mode === "call" && (
            <div>
              <label className="text-white/60 text-xs flex items-center gap-1.5"><User className="w-3 h-3" /> Investor</label>
              <select value={form.investorId} onChange={(e) => setForm({ ...form, investorId: e.target.value })}
                disabled={investorsLoading}
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white">
                <option value="" className="bg-[#111]">{investorsLoading ? "Loading…" : "— Pick an investor —"}</option>
                {investors.map((i: any) => (
                  <option key={i.id} value={i.id} className="bg-[#111]">
                    {i.name}{i.firm ? ` · ${i.firm}` : ""}
                  </option>
                ))}
              </select>
              {!investorsLoading && investors.length === 0 && (
                <p className="text-[11px] text-white/40 mt-1">No investors in your coverage yet.</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/60 text-xs">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white" />
            </div>
            <div>
              <label className="text-white/60 text-xs">Time</label>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white" />
            </div>
          </div>

          {mode === "block" ? (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-white/60 text-xs">Duration (min)</label>
                <input type="number" min={5} step={5} value={form.duration} onChange={(e) => setForm({ ...form, duration: parseInt(e.target.value) || 30 })}
                  className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white" />
              </div>
              <div>
                <label className="text-white/60 text-xs">Kind</label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white">
                  <option value="meeting" className="bg-[#111]">Meeting</option>
                  <option value="founder_call" className="bg-[#111]">Founder call</option>
                  <option value="internal_review" className="bg-[#111]">Internal review</option>
                  <option value="reminder" className="bg-[#111]">Reminder</option>
                </select>
              </div>
              <div>
                <label className="text-white/60 text-xs">Reminder (min before)</label>
                <input type="number" min={0} step={5} value={form.reminderMinutes} onChange={(e) => setForm({ ...form, reminderMinutes: parseInt(e.target.value) || 0 })}
                  className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white" />
              </div>
            </div>
          ) : (
            <div>
              <label className="text-white/60 text-xs">Duration (min)</label>
              <input type="number" min={15} step={15} value={form.duration} onChange={(e) => setForm({ ...form, duration: parseInt(e.target.value) || 30 })}
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white" />
            </div>
          )}

          <div>
            <label className="text-white/60 text-xs">
              Portco {mode === "call" ? "(optional context)" : "(optional, your coverage only)"}
            </label>
            <select value={form.companyId} onChange={(e) => setForm({ ...form, companyId: e.target.value })}
              className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white">
              <option value="" className="bg-[#111]">— None —</option>
              {companies.map((c: any) => <option key={c.id} value={c.id} className="bg-[#111]">{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-white/60 text-xs">{mode === "call" ? "Agenda (sent to the investor)" : "Notes"}</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3} placeholder={mode === "call" ? "What you'll discuss — appears in the calendar invite." : ""}
              className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white resize-none" />
          </div>

          {mode === "call" && (
            <div className="bg-blue-500/[0.04] border border-blue-500/15 rounded-lg p-3 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.autoCreateMeet} onChange={(e) => setForm({ ...form, autoCreateMeet: e.target.checked })}
                  className="mt-0.5 accent-[#FF5C00]" />
                <span className="text-xs text-white/80 flex items-center gap-1.5">
                  <Video className="w-3.5 h-3.5 text-blue-300" />
                  Auto-create Google Meet & send the calendar invite
                </span>
              </label>
              {!form.autoCreateMeet && (
                <div>
                  <label className="text-white/60 text-xs">Meeting link</label>
                  <input value={form.meetingLink} onChange={(e) => setForm({ ...form, meetingLink: e.target.value })}
                    placeholder="https://zoom.us/j/… or meet.google.com/…"
                    className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-3 py-2 mt-1 text-white text-xs" />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={saving}
            className={`flex-1 ${mode === "call" ? "bg-blue-500 hover:bg-blue-600" : "bg-[#FF5C00] hover:bg-[#E55200]"} text-white py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50`}>
            {saving
              ? <><Loader2 className="w-4 h-4 animate-spin" /> {mode === "call" ? "Scheduling…" : "Saving…"}</>
              : mode === "call" ? "Schedule call" : "Create event"}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 border border-white/15 rounded-lg text-sm text-white/70 hover:text-white">Cancel</button>
        </div>
      </div>
    </div>
  );
}
